/**
 * BulkImportLocaisModal - Modal for bulk importing counting locations for multiple hospitals
 *
 * Uses CNPJ as the primary key to identify hospitals.
 */
import React from 'react';
import { Upload, FileSpreadsheet, X as XIcon, Building2, AlertCircle } from 'lucide-react';

interface BulkImportLocaisResult {
  success: boolean;
  hospitais_processados: number;
  locais_criados: number;
  locais_atualizados: number;
  total_locais: number;
  errors: string[];
  cnpjs_nao_encontrados: string[];
}

interface BulkImportLocaisModalProps {
  show: boolean;
  importFile: File | null;
  importing: boolean;
  importResult: BulkImportLocaisResult | null;
  onFileChange: (file: File | null) => void;
  onDownloadTemplate: () => void;
  onImport: () => void;
  onClose: () => void;
}

export const BulkImportLocaisModal: React.FC<BulkImportLocaisModalProps> = ({
  show,
  importFile,
  importing,
  importResult,
  onFileChange,
  onDownloadTemplate,
  onImport,
  onClose,
}) => {
  if (!show) return null;

  const formatCnpj = (cnpj: string): string => {
    if (cnpj.length !== 14) return cnpj;
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Upload em Lote - Locais de Contagem
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Info Banner */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <p className="text-sm text-purple-800 dark:text-purple-200 mb-2 font-medium">
              Importe locais de contagem para vários hospitais de uma vez!
            </p>
            <ul className="text-xs text-purple-700 dark:text-purple-300 list-disc list-inside space-y-1">
              <li>Use o CNPJ para identificar cada hospital</li>
              <li>Cada linha pode ter até 50 locais de contagem</li>
              <li>Locais existentes serão atualizados (sem duplicar)</li>
            </ul>
          </div>

          {/* Download Template */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
              Baixe o template Excel para preencher os dados:
            </p>
            <button
              onClick={onDownloadTemplate}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <FileSpreadsheet className="w-4 h-4" /> Baixar Template
            </button>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arquivo Excel
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
              {importFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{importFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => onFileChange(null)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Clique para selecionar ou arraste o arquivo
                  </p>
                  <p className="text-xs text-gray-400 mt-1">.xlsx ou .xls</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Result */}
          {importResult && (
            <div className="space-y-3">
              {/* Success Stats */}
              {(importResult.locais_criados > 0 || importResult.locais_atualizados > 0) && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-300">
                      {importResult.hospitais_processados} {importResult.hospitais_processados === 1 ? 'hospital processado' : 'hospitais processados'}
                    </span>
                  </div>
                  {importResult.locais_criados > 0 && (
                    <p className="text-sm text-green-700 dark:text-green-300">
                      ✅ {importResult.locais_criados} {importResult.locais_criados === 1 ? 'local criado' : 'locais criados'} com sucesso
                    </p>
                  )}
                  {importResult.locais_atualizados > 0 && (
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      🔄 {importResult.locais_atualizados} {importResult.locais_atualizados === 1 ? 'local atualizado' : 'locais atualizados'}
                    </p>
                  )}
                </div>
              )}

              {/* No data warning */}
              {importResult.total_locais === 0 && importResult.errors.length === 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    ⚠️ Nenhum local encontrado no arquivo. Verifique se o Excel contém dados além do cabeçalho.
                  </p>
                </div>
              )}

              {/* CNPJs not found */}
              {importResult.cnpjs_nao_encontrados && importResult.cnpjs_nao_encontrados.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                      CNPJs não encontrados no sistema:
                    </span>
                  </div>
                  <ul className="text-xs text-orange-600 dark:text-orange-400 list-disc list-inside max-h-20 overflow-y-auto">
                    {importResult.cnpjs_nao_encontrados.map((cnpj, i) => (
                      <li key={i}>{formatCnpj(cnpj)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {importResult.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-700 dark:text-red-300 mb-1 font-medium">
                    ❌ Erros encontrados ({importResult.errors.length}):
                  </p>
                  <ul className="text-xs text-red-600 dark:text-red-400 list-disc list-inside max-h-32 overflow-y-auto">
                    {importResult.errors.slice(0, 20).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {importResult.errors.length > 20 && (
                      <li className="font-medium">... e mais {importResult.errors.length - 20} erros</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            {importResult ? 'Fechar' : 'Cancelar'}
          </button>
          {!importResult && (
            <button
              onClick={onImport}
              disabled={!importFile || importing}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 flex items-center gap-2"
            >
              {importing ? (
                <>
                  <span className="animate-spin">⏳</span> Importando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" /> Importar
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
